import React, { useEffect, useRef, useState } from 'react';

const PARTICLE_COUNT = 1900;
const PARTICLE_COUNT_CONTACT = 600;
const COLOR_PRIMARY = '#58B2DC';
const BG_DARK = '#050505';
const CURSOR_COLOR = '#F7D94C';

// ─── 错开淡入组件 ──────────────────────────────────────────
function Reveal({ children, delay = 0, y = 28, duration = 1100 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : `translateY(${y}px)`,
      filter: visible ? 'blur(0px)' : 'blur(10px)',
      transition: `opacity ${duration}ms cubic-bezier(0.4,0,0.2,1), transform ${duration}ms cubic-bezier(0.4,0,0.2,1), filter ${duration}ms cubic-bezier(0.4,0,0.2,1)`,
    }}>
      {children}
    </div>
  );
}

// ─── 首页组件：Hello → 触发 → 名字淡入 ──────────────────────
function HomeSplit({ typingText, showCursor, navigateTo, revealedRef }) {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const reveal = () => {
      if (!revealed) { setRevealed(true); revealedRef.current = true; }
    };
    window.addEventListener('click', reveal, { once: true });
    window.addEventListener('wheel', reveal, { once: true });
    window.addEventListener('touchstart', reveal, { once: true });
    return () => {
      window.removeEventListener('click', reveal);
      window.removeEventListener('wheel', reveal);
      window.removeEventListener('touchstart', reveal);
    };
  }, [revealed]);

  return (
    <div style={{ height: '100vh', position: 'relative' }}>
      <div style={{
        position: 'fixed', inset: 0, zIndex: 12,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: '0 6vw',
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'translateY(0)' : 'translateY(20px)',
        filter: revealed ? 'blur(0px)' : 'blur(8px)',
        transition: 'opacity .9s ease, transform .9s ease, filter .9s ease',
        pointerEvents: revealed ? 'auto' : 'none',
      }}>
        <p style={{ fontSize: '.65rem', color: '#58B2DC', letterSpacing: '.5em', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
          Portfolio · Showreel · 2025
        </p>
        <h1 style={{ fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1, margin: 0, fontSize: 'clamp(3rem,10vw,9rem)' }}>
          PORTFOLIO FOR
          <span style={{ display: 'block', color: '#58B2DC', marginTop: '.15em', fontSize: 'clamp(2.5rem,8vw,7rem)' }}>Suki Leong</span>
          <span style={{ display: 'block', color: 'rgba(255,255,255,.15)', marginTop: '.2em', fontWeight: 300, letterSpacing: '.6em', fontSize: 'clamp(.9rem,2vw,1.6rem)' }}>梁 洁 微</span>
        </h1>
        <div style={{ marginTop: '2.5rem', fontSize: 'clamp(.9rem,1.8vw,1.3rem)', color: '#aaa', minHeight: '2rem' }}>
          {typingText}{showCursor ? <span style={{ color: '#58B2DC', animation: 'none' }}>|</span> : null}
        </div>
        <div style={{ marginTop: '3rem', display: 'flex', gap: '2rem' }}>
          {['About','Work','Contact'].map(p => (
            <button key={p} onClick={() => navigateTo(p)} style={{ background: 'none', border: '1px solid rgba(255,255,255,.15)', padding: '.6rem 1.8rem', color: '#aaa', fontSize: '.7rem', letterSpacing: '.2em', textTransform: 'uppercase', cursor: 'none', transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#58B2DC'; e.currentTarget.style.color = '#58B2DC'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,.15)'; e.currentTarget.style.color = '#aaa'; }}
            >{p}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Contact 专属粒子 Canvas ──────────────────────────────
function ContactCanvas({ hovering }) {
  const ref = useRef(null);
  const hoverRef = useRef(false);
  useEffect(() => { hoverRef.current = hovering; }, [hovering]);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    let raf, pts = [], rotY = 0, tick = 0;

    // 正确初始化尺寸
    let W = window.innerWidth;
    let H = window.innerHeight;
    const DPR = window.devicePixelRatio || 1;

    const setSize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width  = W * DPR;
      canvas.height = H * DPR;
      canvas.style.width  = W + 'px';
      canvas.style.height = H + 'px';
      // 重置 transform 再 scale，避免叠加
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };

    const build = () => {
      pts = [];
      const R = Math.min(W, H) * 0.52;
      for (let i = 0; i < 700; i++) {
        const theta  = Math.random() * Math.PI * 2;
        const phi    = Math.acos(2 * Math.random() - 1);
        const spread = Math.pow(Math.random(), 0.5);
        const r = R * (0.4 + spread * 0.8);
        const ox = r * Math.sin(phi) * Math.cos(theta);
        const oy = r * Math.sin(phi) * Math.sin(theta);
        const oz = r * Math.cos(phi);
        const rnd = Math.random();
        const alpha = rnd < 0.05 ? 0.65 + Math.random() * 0.35
                    : rnd < 0.18 ? 0.15 + Math.random() * 0.18
                    : Math.random() * 0.07;
        const size  = rnd < 0.05 ? 1.6 + Math.random() * 0.8 : 0.5 + Math.random() * 0.5;
        pts.push({ ox, oy, oz, x: ox, y: oy, z: oz, alpha, size, glow: rnd < 0.05 });
      }
    };

    const draw = () => {
      // 深色背景
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#050505';
      ctx.fillRect(0, 0, W, H);

      rotY += 0.00035;
      tick += 0.012;

      // 球心偏右下出画
      const cx = W * 0.80 + Math.sin(tick * 0.25) * 8;
      const cy = H * 0.72 + Math.cos(tick * 0.18) * 6;
      const FL = 460;
      const isHover = hoverRef.current;
      const cosR = Math.cos(rotY), sinR = Math.sin(rotY);

      pts.forEach(p => {
        // hover 波动
        if (isHover) {
          const wave = Math.sin(tick * 3 + p.ox * 0.018) * 16;
          p.x += (p.ox + wave * 0.4 - p.x) * 0.05;
          p.y += (p.oy + wave * 0.25 - p.y) * 0.05;
        } else {
          p.x += (p.ox - p.x) * 0.03;
          p.y += (p.oy - p.y) * 0.03;
        }
        p.z += (p.oz - p.z) * 0.03;

        // Y 旋转
        const rx = p.x * cosR - p.z * sinR;
        const ry = p.y;
        const rz = p.x * sinR + p.z * cosR;

        const persp = FL / (FL + rz);
        if (persp <= 0) return;
        const sx = rx * persp + cx;
        const sy = ry * persp + cy;
        const sz = Math.max(0.3, p.size * persp);

        const depth = Math.max(0, Math.min(1, (rz + 280) / 560));
        const a = p.alpha * (0.25 + depth * 0.75);

        if (p.glow) {
          // 亮星：晕圈
          ctx.globalAlpha = a * 0.35;
          const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, sz * 6);
          g.addColorStop(0, '#58B2DC');
          g.addColorStop(1, 'rgba(88,178,220,0)');
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(sx, sy, sz * 6, 0, Math.PI * 2);
          ctx.fill();
          // 亮芯
          ctx.globalAlpha = a;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(sx - sz * 0.5, sy - sz * 0.5, sz, sz);
        } else {
          ctx.globalAlpha = a;
          ctx.fillStyle = '#58B2DC';
          ctx.fillRect(sx - sz * 0.5, sy - sz * 0.5, sz, sz);
        }
      });

      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };

    window.addEventListener('resize', () => { setSize(); build(); });
    setSize();
    build();
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', () => { setSize(); build(); });
    };
  }, []);

  return <canvas ref={ref} style={{ position:'fixed', top:0, left:0, zIndex:1, pointerEvents:'none' }} />;
}

// ─── Contact 页面组件 ─────────────────────────────────────
function ContactPageWrapper() {
  const [hovering, setHovering] = useState(false);

  return (
    <div style={{ minHeight:'100vh', position:'relative', overflow:'hidden' }}>

      <ContactCanvas hovering={hovering} />

      {/* 散落文字层 — 提高亮度 */}
      <div style={{ position:'fixed', inset:0, zIndex:2, pointerEvents:'none' }}>
        <div style={{ position:'absolute', left:'4vw', top:'50%', transform:'translateY(-50%)', fontSize:'.6rem', color:'rgba(255,255,255,.42)', lineHeight:1.7, letterSpacing:'.05em' }}>Film<br />Director</div>
        <div style={{ position:'absolute', left:'22vw', top:'38%', fontSize:'.65rem', color:'rgba(255,255,255,.38)', lineHeight:1.8, maxWidth:140 }}>Open for<br />collaboration<br />& new projects</div>
        <div style={{ position:'absolute', left:'48vw', top:'22%', fontSize:'.6rem', color:'rgba(255,255,255,.32)', lineHeight:1.7, letterSpacing:'.03em' }}>Macau · Taipei<br />Shenzhen</div>
        <div style={{ position:'absolute', right:'6vw', top:'18%', fontSize:'.6rem', color:'rgba(255,255,255,.32)', lineHeight:1.7, textAlign:'right' }}>Creative<br />Planner</div>
        <div style={{ position:'absolute', right:'5vw', top:'50%', transform:'translateY(-50%)', fontSize:'.6rem', color:'rgba(255,255,255,.38)', lineHeight:1.8, textAlign:'right', maxWidth:200 }}>If you have a project<br />or creative vision,<br />drop a line.</div>
        <div style={{ position:'absolute', left:'28vw', bottom:'22%', fontSize:'.6rem', color:'rgba(255,255,255,.28)', letterSpacing:'.2em', textTransform:'uppercase' }}>Say Hello</div>
        <div style={{ position:'absolute', left:'50%', bottom:'18%', transform:'translateX(-50%)', fontSize:'.55rem', color:'rgba(255,255,255,.25)', letterSpacing:'.3em', textTransform:'uppercase', whiteSpace:'nowrap' }}>Film · Direction · Creative Planning</div>
      </div>

      {/* 中央主内容 */}
      <div style={{ position:'relative', zIndex:3, minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'0 8vw', boxSizing:'border-box' }}>
        <div style={{ textAlign:'center', marginBottom:'3rem' }}>
          <p style={{ fontSize:'.55rem', color:'rgba(255,255,255,.45)', letterSpacing:'.5em', textTransform:'uppercase', margin:'0 0 1.5rem 0' }}>003 — Contact</p>
          <h1 style={{ fontSize:'clamp(3.5rem,10.5vw,9.8rem)', fontWeight:700, letterSpacing:'-.04em', lineHeight:.85, margin:0, color:'#fff' }}>CONTACT</h1>
        </div>
        <div style={{ display:'flex', gap:'4rem', alignItems:'center' }}>
          <a href="mailto:leong.cereskimi@gmail.com"
            style={{ fontSize:'.765rem', color:'rgba(255,255,255,.7)', textDecoration:'none', letterSpacing:'.08em', transition:'color .25s', borderBottom:'1px solid rgba(255,255,255,.2)', paddingBottom:'.3rem' }}
            onMouseEnter={e => { setHovering(true); e.currentTarget.style.color='#58B2DC'; }}
            onMouseLeave={e => { setHovering(false); e.currentTarget.style.color='rgba(255,255,255,.7)'; }}
          >leong.cereskimi@gmail.com</a>
          <span style={{ color:'rgba(255,255,255,.3)', fontSize:'.7rem' }}>/</span>
          <a href="https://instagram.com/Leong_kitmei" target="_blank" rel="noreferrer"
            style={{ fontSize:'.765rem', color:'rgba(255,255,255,.5)', textDecoration:'none', letterSpacing:'.08em', transition:'color .25s', borderBottom:'1px solid rgba(255,255,255,.15)', paddingBottom:'.3rem' }}
            onMouseEnter={e => { setHovering(true); e.currentTarget.style.color='#58B2DC'; }}
            onMouseLeave={e => { setHovering(false); e.currentTarget.style.color='rgba(255,255,255,.5)'; }}
          >@Leong_kitmei</a>
        </div>
      </div>

      {/* 底部版权 */}
      <div style={{ position:'fixed', bottom:'3.5rem', left:'8vw', right:'8vw', zIndex:4, display:'flex', justifyContent:'space-between' }}>
        <p style={{ fontSize:'.55rem', color:'rgba(255,255,255,.25)', letterSpacing:'.2em', textTransform:'uppercase', margin:0 }}>© 2025 Suki Leong</p>
        <p style={{ fontSize:'.55rem', color:'rgba(255,255,255,.25)', letterSpacing:'.2em', textTransform:'uppercase', margin:0 }}>国立台北艺术大学 电影制作研究所</p>
      </div>

    </div>
  );
}

// ─── About 页面 ──────────────────────────────────────────────
function AboutPage() {
  const containerRef = useRef(null);
  const spotRef = useRef(null);
  const bgRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [spotPos, setSpotPos] = useState({ x: -999, y: -999 });
  const [activeItem, setActiveItem] = useState(null);

  const timeline = [
    { year:'2025', title:'鸿阳广告（深圳）', sub:'导演', type:'work', indent: 0 },
    { year:'2024', title:'福利社艺术展览空间', sub:'社群媒体运营', type:'work', indent: 6 },
    { year:'2022–25', title:'国立台北艺术大学', sub:'电影制作研究所', type:'edu', indent: 2 },
    { year:'2021', title:'Togather Story（澳门）', sub:'编导 + 制片', type:'work', indent: 9 },
    { year:'2017–21', title:'澳门大学', sub:'传播系 · 创意媒体方向', type:'edu', indent: 4 },
  ];

  const filmExp = [
    { year:'2025', title:'《日落的声音》', role:'第二副导演', org:'澳门新势力短片' },
    { year:'2024', title:'《倾斜的秤》', role:'场记', org:'台湾公视学生剧展' },
    { year:'2024', title:'《白雪》', role:'美术组实习生', org:'高雄拍短片' },
    { year:'2023', title:'《有生之年》', role:'制片组实习生', org:'台湾电视剧' },
    { year:'2023', title:'《近视》', role:'场记', org:'111年度电影短片辅导金入选作品' },
    { year:'2023', title:'《杯面》', role:'编导、剪辑', org:'学生短片制作' },
    { year:'2023', title:'《409.9MHz》', role:'制片', org:'学生短片制作' },
    { year:'2023', title:'台湾国际女性影展', role:'节目剪辑组实习生', org:'' },
  ];

  const skills = ['导演','制片','场记','编剧','Adobe Premiere','Illustrator','AIGC','纪实拍摄','广告策划'];

  // 视差滚动
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => setScrollY(el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // 追光灯跟随鼠标
  useEffect(() => {
    const onMove = (e) => {
      setSpotPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div ref={containerRef} style={{ height:'100vh', overflowY:'auto', position:'relative', scrollbarWidth:'thin', scrollbarColor:'#58B2DC22 transparent' }}>

      {/* 追光灯层 */}
      <div style={{
        position:'fixed', zIndex:3, pointerEvents:'none',
        left: spotPos.x, top: spotPos.y,
        width: 420, height: 420,
        transform:'translate(-50%,-50%)',
        background:'radial-gradient(circle, rgba(88,178,220,0.07) 0%, transparent 70%)',
        transition:'left 0.8s cubic-bezier(0.25,0.46,0.45,0.94), top 0.8s cubic-bezier(0.25,0.46,0.45,0.94)',
        borderRadius:'50%',
      }} />

      {/* 背景：粒子 canvas（由主 canvas 负责） */}

      {/* LEONG 大字背景层 — 视差中等，内容从上滑过 */}
      <div style={{
        position:'fixed', zIndex:2, top:0, left:0, right:0, pointerEvents:'none',
        display:'flex', alignItems:'center', justifyContent:'flex-start',
        padding:'5rem 8vw 0',
        transform:`translateY(${-scrollY * 0.3}px)`,
        transition:'transform 0.1s linear',
        userSelect:'none',
      }}>
        <div style={{ fontSize:'clamp(7rem,20vw,18rem)', fontWeight:700, letterSpacing:'-.05em', lineHeight:1, color:'rgba(255,255,255,.035)', whiteSpace:'nowrap' }}>
          LEONG
        </div>
      </div>

      {/* 内容层 — 正常滚动速度 */}
      <div style={{ position:'relative', zIndex:4, padding:'0', width:'100%', boxSizing:'border-box' }}>

        {/* ── Hero 区 ── */}
        <Reveal delay={0} y={40} duration={1200}>
        <div style={{ padding:'8rem 10vw 3rem', display:'flex', alignItems:'flex-end', justifyContent:'space-between', borderBottom:'1px solid rgba(255,255,255,.05)' }}>
          <div>
            <p style={{ fontSize:'.6rem', color:'#58B2DC', letterSpacing:'.5em', textTransform:'uppercase', marginBottom:'1.5rem' }}>001 — About</p>
            <h2 style={{ fontSize:'clamp(3.5rem,9vw,8rem)', fontWeight:700, letterSpacing:'-.04em', lineHeight:.9, margin:0 }}>
              LEONG<br />
              <span style={{ fontWeight:300, fontStyle:'italic', color:'rgba(255,255,255,.35)', fontSize:'clamp(2.5rem,7vw,6.5rem)' }}>Kit Mei</span>
            </h2>
          </div>
          <div style={{ textAlign:'right', paddingBottom:'.4rem' }}>
            <p style={{ fontSize:'.65rem', color:'rgba(255,255,255,.35)', letterSpacing:'.3em', textTransform:'uppercase', margin:'0 0 .35rem 0' }}>Film Director</p>
            <p style={{ fontSize:'.65rem', color:'rgba(255,255,255,.35)', letterSpacing:'.3em', textTransform:'uppercase', margin:'0 0 .35rem 0' }}>Creative Planner</p>
            <p style={{ fontSize:'.65rem', color:'rgba(255,255,255,.25)', letterSpacing:'.3em', textTransform:'uppercase', margin:0 }}>Macau · Taipei · Shenzhen</p>
          </div>
        </div>
        </Reveal>

        {/* ── 主体：35% 文字 + 65% 时间轴 ── */}
        <div style={{ padding:'5rem 10vw 8rem', display:'grid', gridTemplateColumns:'35fr 65fr', gap:'6rem', alignItems:'start' }}>

          {/* 左栏：介绍 + 漂浮技能 */}
          <div>
            <Reveal delay={200} y={30}>
            <p style={{ fontSize:'clamp(1rem,2vw,1.5rem)', color:'rgba(255,255,255,.8)', lineHeight:1.7, fontWeight:300, fontStyle:'italic', margin:'0 0 2.5rem 0', letterSpacing:'-.01em' }}>
              "每一格画面都是<br/>一次对真实的<br/>重新诠释。"
            </p>
            </Reveal>
            <Reveal delay={450} y={20}>
            <p style={{ fontSize:'.82rem', color:'rgba(255,255,255,.55)', lineHeight:2.1, margin:'0 0 1.25rem 0' }}>影像创作充满热情，在过去的学习和实践中尝试过导演、制片、场记等不同职位，累积到多元经验。</p>
            </Reveal>
            <Reveal delay={650} y={20}>
            <p style={{ fontSize:'.82rem', color:'rgba(255,255,255,.55)', lineHeight:2.1, margin:'0 0 1.25rem 0' }}>也具备剪辑和视觉设计的经验，熟悉 Adobe Premiere 和 Illustrator 等软体，能够处理基本的影片剪辑及影像处理工作。</p>
            </Reveal>
            <Reveal delay={800} y={20}>
            <p style={{ fontSize:'.82rem', color:'rgba(255,255,255,.55)', lineHeight:2.1, margin:'0 0 3rem 0' }}>擅长运用 AI 工具辅助创作——熟练使用可灵 AI、豆包、Gemini 生成分镜，大幅缩短分镜制作时间，能快速响应客户改稿需求。曾运用这套流程提前交付成片。</p>
            </Reveal>

            {/* 漂浮技能标签 — 片尾字幕式上飘 */}
            <Reveal delay={850} y={10}>
            <p style={{ fontSize:'.5rem', color:'rgba(88,178,220,.5)', letterSpacing:'.4em', textTransform:'uppercase', margin:'0 0 1rem 0' }}>Disciplines</p>
            <div style={{ overflow:'hidden', position:'relative', height: 120 }}>
              <style>{`
                @keyframes floatUp {
                  0%   { transform: translateY(0); opacity: 0.5; }
                  50%  { opacity: 0.7; }
                  100% { transform: translateY(-100%); opacity: 0; }
                }
                .skill-tag { animation: floatUp linear infinite; }
                .skill-tag:hover { opacity: 1 !important; color: #58B2DC !important; animation-play-state: paused; }
              `}</style>
              {skills.map((s, i) => (
                <div key={s} className="skill-tag" style={{
                  position:'absolute',
                  left: `${(i % 3) * 33}%`,
                  bottom: `${Math.floor(i / 3) * 40}px`,
                  fontSize:'.68rem',
                  color:'rgba(255,255,255,.38)',
                  letterSpacing:'.08em',
                  whiteSpace:'nowrap',
                  cursor:'default',
                  animationDuration: `${7 + i * 1.3}s`,
                  animationDelay: `${i * 0.8}s`,
                  transition:'color .2s',
                }}>
                  {s}
                </div>
              ))}
            </div>
            </Reveal>
          </div>

          {/* 右栏：两列并排 — 左工作时间轴，右影像制作经验 */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'3rem', alignItems:'start' }}>

            {/* 左：工作时间轴 */}
            <div>
            <Reveal delay={300} y={0} duration={800}>
            <p style={{ fontSize:'.5rem', color:'rgba(88,178,220,.5)', letterSpacing:'.4em', textTransform:'uppercase', margin:'0 0 .4rem 0' }}>Work & Education</p>
            <p style={{ fontSize:'.55rem', color:'rgba(255,255,255,.2)', letterSpacing:'.1em', margin:'0 0 3rem 0' }}>工作 & 教育</p>

            {timeline.map((item, i) => (
              <div
                key={i}
                onMouseEnter={() => setActiveItem(i)}
                onMouseLeave={() => setActiveItem(null)}
                style={{
                  display:'grid',
                  gridTemplateColumns:'4.5rem 1fr',
                  gap:'1.5rem',
                  paddingBottom:'2.5rem',
                  marginLeft: `${item.indent}%`,
                  position:'relative',
                  transition:'margin-left 0.4s ease',
                  cursor:'default',
                }}
              >
                <div style={{ paddingTop:'.15rem', textAlign:'right' }}>
                  <span style={{
                    fontSize: item.type==='work' ? '1rem' : '.7rem',
                    fontWeight: item.type==='work' ? 700 : 300,
                    fontStyle: item.type==='edu' ? 'italic' : 'normal',
                    color: item.type==='work' ? (activeItem===i ? '#58B2DC' : 'rgba(88,178,220,.7)') : 'rgba(255,255,255,.2)',
                    letterSpacing: item.type==='work' ? '-.02em' : '.05em',
                    transition:'color .3s',
                  }}>{item.year}</span>
                </div>
                <div style={{ borderLeft:`1px solid ${activeItem===i ? 'rgba(88,178,220,.4)' : 'rgba(255,255,255,.05)'}`, paddingLeft:'1.25rem', transition:'border-color .3s' }}>
                  <p style={{ fontSize: item.type==='work'?'.95rem':'.82rem', fontWeight:item.type==='work'?500:300, color:activeItem===i?'#fff':(item.type==='work'?'rgba(255,255,255,.8)':'rgba(255,255,255,.4)'), margin:'0 0 .3rem 0', letterSpacing:'-.01em', transition:'color .3s' }}>{item.title}</p>
                  <p style={{ fontSize:'.68rem', color:activeItem===i?'rgba(88,178,220,.8)':'rgba(255,255,255,.22)', margin:0, letterSpacing:'.08em', transition:'color .3s' }}>{item.sub}</p>
                </div>
              </div>
            ))}
            </Reveal>
            </div>

            {/* 右：影像制作经验 */}
            <div>
            <Reveal delay={450} y={0} duration={800}>
            <p style={{ fontSize:'.5rem', color:'rgba(88,178,220,.5)', letterSpacing:'.4em', textTransform:'uppercase', margin:'0 0 .4rem 0' }}>Film & TV Production</p>
            <p style={{ fontSize:'.55rem', color:'rgba(255,255,255,.2)', letterSpacing:'.1em', margin:'0 0 3rem 0' }}>影像制作经验</p>
            {filmExp.map((item, i) => (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'3.5rem 1fr', gap:'1rem', paddingBottom:'1.6rem' }}>
                <div style={{ paddingTop:'.1rem', textAlign:'right' }}>
                  <span style={{ fontSize:'.62rem', fontWeight:300, color:'rgba(255,255,255,.18)', letterSpacing:'.03em' }}>{item.year}</span>
                </div>
                <div style={{ borderLeft:'1px solid rgba(255,255,255,.05)', paddingLeft:'1rem' }}>
                  <p style={{ fontSize:'.82rem', fontWeight:500, color:'rgba(255,255,255,.7)', margin:'0 0 .2rem 0', letterSpacing:'-.01em' }}>{item.title}</p>
                  <p style={{ fontSize:'.62rem', color:'rgba(88,178,220,.45)', margin:'0 0 .15rem 0', letterSpacing:'.05em' }}>{item.role}</p>
                  {item.org && <p style={{ fontSize:'.58rem', color:'rgba(255,255,255,.15)', margin:0, letterSpacing:'.06em' }}>{item.org}</p>}
                </div>
              </div>
            ))}
            </Reveal>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}

// ─── AI 图片画廊 ──────────────────────────────────────────
// 图片放在 public/ai/ 目录，命名 ai-01.jpg ~ ai-32.jpg
// portrait: true = 9:16竖版，false = 16:9横版
const AI_IMAGES = [
  { id:1,  src:'/ai/ai-01.jpg',  portrait:false },
  { id:2,  src:'/ai/ai-02.jpg',  portrait:false, prompt:'极端微距摄影照片。特写镜头，一只手拿着一个小巧的半透明塑料眼药水瓶。瓶子倾斜着，一滴清澈的水珠正在瓶尖形成，位置正对一个人的眼睛上方。这只眼睛正向上看。手上和手指的皮肤纹理、瓶子上的冷凝水汽、以及眼睛湿润的表面细节都清晰可见。自然光产生反射。照片级真实感。' },
  { id:3,  src:'/ai/ai-03.jpg',  portrait:false },
  { id:4,  src:'/ai/ai-04.jpg',  portrait:false },
  { id:5,  src:'/ai/ai-05.jpg',  portrait:false },
  { id:6,  src:'/ai/ai-06.jpg',  portrait:false },
  { id:7,  src:'/ai/ai-07.jpg',  portrait:false, prompt:'Extreme close-up of a person\'s mouth biting into a crispy fried chicken, golden crispy texture, blurry background, high speed photography, appetizing.' },
  { id:8,  src:'/ai/ai-08.jpg',  portrait:false },
  { id:9,  src:'/ai/ai-09.jpg',  portrait:false },
  { id:10, src:'/ai/ai-10.jpg',  portrait:false },
  { id:11, src:'/ai/ai-11.jpg',  portrait:false },
  { id:12, src:'/ai/ai-12.jpg',  portrait:false },
  { id:13, src:'/ai/ai-13.jpg', portrait:false },
  { id:14, src:'/ai/ai-14.jpg',  portrait:false },
  { id:15, src:'/ai/ai-15.jpg',  portrait:true  },
  { id:16, src:'/ai/ai-16.jpg',  portrait:true  },
  { id:17, src:'/ai/ai-17.jpg',  portrait:true  },
  { id:18, src:'/ai/ai-18.jpg',  portrait:false },
  { id:19, src:'/ai/ai-19.jpg',  portrait:false },
  { id:20, src:'/ai/ai-20.jpg',  portrait:false },
  { id:21, src:'/ai/ai-21.jpg',  portrait:false },
  { id:22, src:'/ai/ai-22.jpg',  portrait:false },
  { id:23, src:'/ai/ai-23.jpg',  portrait:false },
  { id:24, src:'/ai/ai-24.jpg',  portrait:false },
  { id:25, src:'/ai/ai-25.jpg',  portrait:false },
  { id:26, src:'/ai/ai-26.jpg',  portrait:false },
  { id:27, src:'/ai/ai-27.jpg',  portrait:false },
  { id:28, src:'/ai/ai-28.jpg',  portrait:false },
  { id:29, src:'/ai/ai-29.jpg',  portrait:false },
  { id:30, src:'/ai/ai-30.jpg',  portrait:false },
  { id:31, src:'/ai/ai-31.jpg',  portrait:false, prompt:'商业牙科摄影，刷牙的特写，完美的洁白牙齿，薄荷蓝色的牙膏泡沫，充满活力和清新的氛围，高调照明，柔光箱照明，闪亮的湿润嘴唇，构图干净，卫生概念，专业调色，广告风格，高细节，4k。' },
  { id:32, src:'/ai/ai-32.jpg',  portrait:false },
];

function AIGallery({ onBack }) {
  const [preview, setPreview] = useState(AI_IMAGES[0]);
  const [paused, setPaused] = useState(false);
  const [rotation, setRotation] = useState(0);
  const rafRef = useRef(null);
  const rotRef = useRef(0);
  const pausedRef = useRef(false);
  const speedRef = useRef(0.04);

  const COUNT = AI_IMAGES.length;
  const RADIUS = 420;
  const CARD_W = 75;
  const CARD_H = 100;

  useEffect(() => {
    const animate = () => {
      if (!pausedRef.current) {
        rotRef.current += speedRef.current;
        setRotation(rotRef.current);
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const handleHover = (img) => { pausedRef.current = true; setPaused(true); setPreview(img); };
  const handleLeave = () => { pausedRef.current = false; setPaused(false); };

  const arrowBtn = (dir) => ({
    position:'absolute', top:'50%', transform:'translateY(-50%)',
    [dir === 'left' ? 'left' : 'right']: '3vw',
    zIndex:20, background:'none', border:'1px solid rgba(255,255,255,.15)',
    color:'rgba(255,255,255,.4)', fontSize:'1.2rem', width:40, height:40,
    display:'flex', alignItems:'center', justifyContent:'center',
    cursor:'none', transition:'all .2s', borderRadius:0,
  });

  return (
    <div style={{ height:'100vh', overflow:'hidden', position:'relative', background:BG_DARK }}>
      <style>{`
        .ai-card {
          position: absolute;
          width: ${CARD_W}px;
          height: ${CARD_H}px;
          transform-style: preserve-3d;
          cursor: none;
        }
        .ai-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          border-radius: 4px;
          filter: brightness(0.75);
          transition: filter 0.3s ease, transform 0.3s ease;
        }
        .ai-card:hover img {
          filter: brightness(1.1);
          transform: scale(1.05);
        }
        @keyframes previewFade {
          from { opacity:0; transform: translateY(8px); }
          to   { opacity:1; transform: translateY(0); }
        }
        .preview-fade { animation: previewFade 0.35s ease forwards; }
        .arrow-btn:hover { border-color: #58B2DC !important; color: #58B2DC !important; }
        @keyframes fadeUp {
          0%   { opacity:1; transform: translateX(-50%) translateY(0); }
          80%  { opacity:1; transform: translateX(-50%) translateY(-10px); }
          100% { opacity:0; transform: translateX(-50%) translateY(-14px); }
        }
        @keyframes catBounce {
          0%, 100% { transform: scaleX(-1) translateY(0); }
          50%       { transform: scaleX(-1) translateY(-3px); }
        }
      `}</style>

      {/* 顶部导航 — 与实拍作品同款位置，在全局导航下方 */}
      <div style={{ position:'absolute', top:'4.5rem', left:0, right:0, zIndex:20, display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'0 10vw' }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:'1.5rem' }}>
          <span style={{ fontSize:'.6rem', color:'rgba(88,178,220,.5)', letterSpacing:'.5em', textTransform:'uppercase' }}>002 —</span>
          <span style={{ fontWeight:300, fontStyle:'italic', color:'rgba(255,255,255,.3)', fontSize:'.6rem', letterSpacing:'.5em', textTransform:'uppercase' }}>AI Works</span>
        </div>
        <button onClick={onBack}
          style={{ background:'none', border:'none', padding:0, fontSize:'.6rem', color:'rgba(255,255,255,.25)', letterSpacing:'.3em', textTransform:'uppercase', cursor:'none', transition:'color .25s' }}
          onMouseEnter={e => e.currentTarget.style.color='#58B2DC'}
          onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,.25)'}
        >← All Works</button>
      </div>

      {/* 预览图 — 居中，稍低，大10% */}
      <div style={{ position:'absolute', top:'10rem', left:'50%', transform:'translateX(-50%)', zIndex:20, textAlign:'center' }}>
        <img
          key={preview.src}
          src={preview.src}
          alt="preview"
          className="preview-fade"
          style={{
            maxWidth: preview.portrait ? 286 : 616,
            maxHeight: '42vh',
            aspectRatio: preview.portrait ? '9/16' : '16/9',
            objectFit: 'contain',
            display:'block',
            borderRadius:4,
            boxShadow:'0 8px 32px rgba(0,0,0,0.6)',
          }}
        />
        <p style={{ fontSize:'.55rem', color:'rgba(255,255,255,.35)', letterSpacing:'.15em', margin:'.75rem 0 0 0' }}>
          {String(AI_IMAGES.findIndex(i=>i.src===preview.src)+1).padStart(2,'0')} / {String(COUNT).padStart(2,'0')}
        </p>
        {preview.prompt && (
          <p key={preview.src + '-prompt'} className="preview-fade" style={{
            maxWidth: 560,
            margin:'.6rem auto 0',
            fontSize:'.6rem',
            color:'rgba(255,255,255,.28)',
            lineHeight:1.8,
            letterSpacing:'.04em',
            fontStyle:'italic',
            textAlign:'left',
            padding:'0 .5rem',
          }}>
            ↳ {preview.prompt}
          </p>
        )}
      </div>

      {/* 3D 圆环 */}
      <div style={{
        position:'absolute',
        left:'50%', top:'78%',
        transform:'translate(-50%, -50%)',
        width:0, height:0,
        perspective: 900,
        perspectiveOrigin:'50% 50%',
        zIndex:10,
      }}>
        {/* 左箭头 — 圆环左边缘外侧 */}
        <button
          className="arrow-btn"
          style={{ position:'fixed', left:`calc(50% - ${RADIUS + 60}px)`, top:'78%', transform:'translateY(-50%)', zIndex:20, background:'none', border:'1px solid rgba(255,255,255,.15)', color:'rgba(255,255,255,.4)', fontSize:'1.2rem', width:40, height:40, display:'flex', alignItems:'center', justifyContent:'center', cursor:'none', transition:'all .2s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='#58B2DC'; e.currentTarget.style.color='#58B2DC'; speedRef.current = -0.25; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.15)'; e.currentTarget.style.color='rgba(255,255,255,.4)'; speedRef.current = 0.04; }}
        >←</button>
        {/* 右箭头 — 圆环右边缘外侧 */}
        <button
          className="arrow-btn"
          style={{ position:'fixed', left:`calc(50% + ${RADIUS + 20}px)`, top:'78%', transform:'translateY(-50%)', zIndex:20, background:'none', border:'1px solid rgba(255,255,255,.15)', color:'rgba(255,255,255,.4)', fontSize:'1.2rem', width:40, height:40, display:'flex', alignItems:'center', justifyContent:'center', cursor:'none', transition:'all .2s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='#58B2DC'; e.currentTarget.style.color='#58B2DC'; speedRef.current = 0.25; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.15)'; e.currentTarget.style.color='rgba(255,255,255,.4)'; speedRef.current = 0.04; }}
        >→</button>

        <div style={{ transformStyle:'preserve-3d', position:'relative', width:0, height:0 }}>
          {AI_IMAGES.map((img, i) => {
            const angle = (360 / COUNT) * i + rotation;
            const rad = (angle * Math.PI) / 180;
            const x = Math.sin(rad) * RADIUS;
            const z = Math.cos(rad) * RADIUS;
            const rotY = -angle;
            return (
              <div key={img.id} className="ai-card"
                style={{
                  transform: `translate3d(${x - CARD_W/2}px, ${-CARD_H/2}px, ${z}px) rotateY(${rotY}deg)`,
                  zIndex: Math.round(z + RADIUS),
                  opacity: z < -RADIUS * 0.3 ? 0.2 + 0.8 * ((z + RADIUS) / (RADIUS * 1.7)) : 1,
                }}
                onMouseEnter={() => handleHover(img)}
                onMouseLeave={handleLeave}
              >
                <img src={img.src} alt="" />
              </div>
            );
          })}
        </div>
      </div>

      {/* 底部提示 */}
      <div style={{ position:'absolute', bottom:'2.5rem', left:'50%', transform:'translateX(-50%)', fontSize:'.55rem', color:'rgba(255,255,255,.2)', letterSpacing:'.3em', textTransform:'uppercase', whiteSpace:'nowrap' }}>
        Hover to pause · {COUNT} works
      </div>
    </div>
  );
}


// ─── Work 分类选择页 ──────────────────────────────────────
function WorkLanding({ onSelect }) {
  const [hovered, setHovered] = useState(null);

  const categories = [
    {
      id: 'film',
      label: 'Live Action',
      sub: '实拍作品',
      desc: 'Commercial · Short Film · Documentary',
      count: '8 works',
      num: '01',
    },
    {
      id: 'ai',
      label: 'AI Works',
      sub: 'AIGC 创作',
      desc: 'Generative · Mixed Workflow · Experimental',
      count: '1 work',
      num: '02',
    },
  ];

  return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', justifyContent:'center', padding:'0 10vw', position:'relative' }}>

      {/* 装饰大字 */}
      <div style={{ position:'absolute', top:'4rem', right:'6vw', fontSize:'clamp(8rem,22vw,20rem)', fontWeight:700, letterSpacing:'-.05em', lineHeight:1, color:'rgba(255,255,255,.025)', pointerEvents:'none', userSelect:'none' }}>
        WORK
      </div>

      {/* 页头 */}
      <Reveal delay={0} y={28} duration={1000}>
      <div style={{ marginBottom:'5rem' }}>
        <p style={{ fontSize:'.6rem', color:'rgba(88,178,220,.5)', letterSpacing:'.5em', textTransform:'uppercase', margin:'0 0 1rem 0' }}>002 — Work</p>
        <div style={{ display:'flex', alignItems:'center', gap:'2rem' }}>
          <div style={{ height:1, width:48, background:'rgba(88,178,220,.25)' }} />
          <p style={{ fontSize:'.6rem', color:'rgba(255,255,255,.2)', letterSpacing:'.3em', textTransform:'uppercase', margin:0 }}>Select a category</p>
        </div>
      </div>
      </Reveal>

      {/* 两个选项 */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2rem', position:'relative', zIndex:1 }}>
        {categories.map((cat, i) => (
          <Reveal key={cat.id} delay={200 + i * 180} y={40} duration={1000}>
          <div
            onClick={() => onSelect(cat.id)}
            onMouseEnter={() => setHovered(cat.id)}
            onMouseLeave={() => setHovered(null)}
            style={{
              padding:'3.5rem',
              border: `1px solid ${hovered===cat.id ? 'rgba(88,178,220,.4)' : 'rgba(255,255,255,.07)'}`,
              cursor:'none',
              position:'relative',
              overflow:'hidden',
              transition:'border-color .4s ease, transform .4s ease',
              transform: hovered===cat.id ? 'translateY(-6px)' : 'translateY(0)',
            }}
          >
            {/* 序号装饰 */}
            <div style={{
              position:'absolute', top:'1.5rem', right:'2rem',
              fontSize:'5rem', fontWeight:700, letterSpacing:'-.05em', lineHeight:1,
              color: hovered===cat.id ? 'rgba(88,178,220,.08)' : 'rgba(255,255,255,.04)',
              transition:'color .4s',
              userSelect:'none',
            }}>{cat.num}</div>

            {/* 悬停光晕 */}
            <div style={{
              position:'absolute', inset:0,
              background:'radial-gradient(ellipse at 30% 50%, rgba(88,178,220,0.06) 0%, transparent 70%)',
              opacity: hovered===cat.id ? 1 : 0,
              transition:'opacity .5s ease',
            }} />

            <div style={{ position:'relative' }}>
              <p style={{ fontSize:'.55rem', color: hovered===cat.id ? '#58B2DC' : 'rgba(255,255,255,.25)', letterSpacing:'.4em', textTransform:'uppercase', margin:'0 0 1.2rem 0', transition:'color .3s' }}>{cat.sub}</p>
              <h3 style={{ fontSize:'clamp(2rem,4vw,3.5rem)', fontWeight:700, letterSpacing:'-.03em', lineHeight:.9, margin:'0 0 1.5rem 0', color: hovered===cat.id ? '#fff' : 'rgba(255,255,255,.75)', transition:'color .3s' }}>{cat.label}</h3>
              <p style={{ fontSize:'.65rem', color:'rgba(255,255,255,.22)', letterSpacing:'.1em', margin:'0 0 2.5rem 0', lineHeight:1.7 }}>{cat.desc}</p>

              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ fontSize:'.6rem', color: hovered===cat.id ? 'rgba(88,178,220,.7)' : 'rgba(255,255,255,.15)', letterSpacing:'.3em', textTransform:'uppercase', transition:'color .3s' }}>{cat.count}</span>
                <span style={{ fontSize:'1.2rem', color: hovered===cat.id ? '#58B2DC' : 'rgba(255,255,255,.2)', transition:'color .3s, transform .3s', display:'inline-block', transform: hovered===cat.id ? 'translateX(4px)' : 'translateX(0)' }}>→</span>
              </div>
            </div>
          </div>
          </Reveal>
        ))}
      </div>

    </div>
  );
}

// ─── 焦点画廊组件 ──────────────────────────────────────────
function FocusGallery({ projects, onSelect, initialIndex = 0 }) {
  const [current, setCurrent] = useState(initialIndex);
  const [hoveredCenter, setHoveredCenter] = useState(false);
  const total = projects.length;

  const prev = () => setCurrent(i => (i - 1 + total) % total);
  const next = () => setCurrent(i => (i + 1) % total);

  // 每张图有固定的 DOM slot（0~4），通过计算偏移量决定哪张图在哪个位置
  // slot 0=最左, 1=左, 2=中, 3=右, 4=最右
  const slotStyles = [
    { x: '-114%', scale: 0.55, zIndex: 1, opacity: 0.3  },
    { x: '-57%',  scale: 0.72, zIndex: 2, opacity: 0.65 },
    { x:    '0%', scale: 1.00, zIndex: 3, opacity: 1.00 },
    { x:   '57%', scale: 0.72, zIndex: 2, opacity: 0.65 },
    { x:  '114%', scale: 0.55, zIndex: 1, opacity: 0.3  },
  ];

  // 每张项目对应的 slot 位置（-2~+2 相对 current）
  const getSlot = (projIdx) => {
    let offset = projIdx - current;
    // 绕环处理，选最近的方向
    if (offset > total / 2) offset -= total;
    if (offset < -total / 2) offset += total;
    return offset + 2; // slot index 0~4
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '56vw', maxHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>

      {/* 每个项目一个固定 DOM，slot 决定位置 */}
      {projects.map((p, idx) => {
        const slot = getSlot(idx);
        const isCenter = slot === 2;
        const isVisible = slot >= 0 && slot <= 4;
        const s = isVisible ? slotStyles[slot] : { x: slot < 0 ? '-150%' : '150%', scale: 0.4, zIndex: 0, opacity: 0 };

        return (
          <div
            key={p.id}
            onClick={() => isCenter ? onSelect(p, current) : setCurrent(idx)}
            onMouseEnter={() => isCenter && setHoveredCenter(true)}
            onMouseLeave={() => setHoveredCenter(false)}
            style={{
              position: 'absolute',
              width: '42vw',
              maxWidth: 680,
              aspectRatio: '16/9',
              borderRadius: 10,
              overflow: 'hidden',
              cursor: 'none',
              zIndex: s.zIndex,
              opacity: s.opacity,
              transform: `translateX(${s.x}) scale(${s.scale})`,
              // 核心：固定 DOM + CSS transition 实现横滑
              transition: 'transform 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.6s cubic-bezier(0.4,0,0.2,1), box-shadow 0.4s ease',
              border: isCenter ? '1px solid rgba(88,178,220,0.25)' : '1px solid rgba(255,255,255,0.05)',
              boxShadow: isCenter ? '0 8px 60px rgba(88,178,220,0.15)' : 'none',
              pointerEvents: isVisible ? 'auto' : 'none',
            }}
          >
            <img
              src={p.image}
              alt={p.title}
              style={{
                width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                filter: isCenter ? 'brightness(0.88)' : 'brightness(0.45) grayscale(30%)',
                transform: isCenter && hoveredCenter ? 'scale(1.04)' : 'scale(1)',
                transition: 'transform 0.65s ease, filter 0.5s ease',
              }}
            />

            {/* 中央图信息覆层 */}
            {isCenter && (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.15) 55%, transparent 100%)',
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                padding: '1.5rem 1.75rem',
                opacity: hoveredCenter ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}>
                <p style={{ fontSize: '.6rem', color: '#58B2DC', letterSpacing: '.3em', textTransform: 'uppercase', margin: '0 0 .35rem 0' }}>{p.category} · {p.role}</p>
                <p style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff', margin: '0 0 .25rem 0', lineHeight: 1.2 }}>{p.title}</p>
                <p style={{ fontSize: '.72rem', color: '#bbb', margin: '0 0 .75rem 0' }}>{p.brand}</p>
                <p style={{ fontSize: '.58rem', color: '#58B2DC', letterSpacing: '.2em', textTransform: 'uppercase' }}>Click to explore →</p>
              </div>
            )}

            {/* 非中央：序号 */}
            {!isCenter && isVisible && (
              <div style={{ position: 'absolute', top: 12, left: 14, fontSize: '.58rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '.15em' }}>
                {String(idx + 1).padStart(2, '0')}
              </div>
            )}
          </div>
        );
      })}

      {/* 左箭头 — 紧贴中央图左侧 */}
      <button onClick={prev} style={{ position:'absolute', left:'calc(50% - 21vw - 52px)', zIndex:10, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'50%', width:44, height:44, display:'flex', alignItems:'center', justifyContent:'center', cursor:'none', color:'#fff', fontSize:'1rem', transition:'background .25s, border-color .25s', backdropFilter:'blur(8px)' }}
        onMouseEnter={e=>{e.currentTarget.style.background='rgba(88,178,220,0.2)';e.currentTarget.style.borderColor='#58B2DC'}}
        onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.06)';e.currentTarget.style.borderColor='rgba(255,255,255,0.1)'}}>←</button>

      {/* 右箭头 — 紧贴中央图右侧 */}
      <button onClick={next} style={{ position:'absolute', left:'calc(50% + 21vw + 8px)', zIndex:10, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'50%', width:44, height:44, display:'flex', alignItems:'center', justifyContent:'center', cursor:'none', color:'#fff', fontSize:'1rem', transition:'background .25s, border-color .25s', backdropFilter:'blur(8px)' }}
        onMouseEnter={e=>{e.currentTarget.style.background='rgba(88,178,220,0.2)';e.currentTarget.style.borderColor='#58B2DC'}}
        onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.06)';e.currentTarget.style.borderColor='rgba(255,255,255,0.1)'}}>→</button>

      {/* 底部分页点 */}
      <div style={{ position:'absolute', bottom:-28, left:'50%', transform:'translateX(-50%)', display:'flex', gap:6 }}>
        {projects.map((_, i) => (
          <div key={i} onClick={() => setCurrent(i)} style={{ width: i===current ? 20 : 6, height:6, borderRadius:3, background: i===current ? '#58B2DC' : 'rgba(255,255,255,0.2)', cursor:'none', transition:'all 0.35s ease' }} />
        ))}
      </div>
    </div>
  );
}

// ─── 主应用 ────────────────────────────────────────────────
export default function App() {
  const canvasRef = useRef(null);
  const [typingText, setTypingText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [activePage, setActivePage] = useState('Home');
  const [selectedProject, setSelectedProject] = useState(null);
  const [workCategory, setWorkCategory] = useState(null); // null=选择页 | "film" | "ai"
  const [savedGalleryIndex, setSavedGalleryIndex] = useState(0);
  const [pageVisible, setPageVisible] = useState(true);
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  const activePageRef = useRef('Home');
  const homeRevealedRef = useRef(false); // 控制 canvas 是否画 Hello

  // 用一个 displayPage 来控制实际渲染的页面，切换时先消失再出现
  const [displayPage, setDisplayPage] = useState('Home');

  const navigateTo = (page) => {
    if (page === activePage) return;
    setPageVisible(false);
    setTimeout(() => {
      setActivePage(page);
      setDisplayPage(page);
      activePageRef.current = page;
      homeRevealedRef.current = false; // 回到首页时重置 Hello
      setSelectedProject(null);
      setWorkCategory(null);
      setPageVisible(true);
    }, 500);
  };

  // 打字机（仅首页）
  useEffect(() => {
    if (activePage !== 'Home') return;
    setTypingText('');
    let i = 0;
    const fullText = 'Film Director & Creative Planner';
    const t = setInterval(() => {
      if (i <= fullText.length) { setTypingText(fullText.slice(0, i)); i++; }
      else clearInterval(t);
    }, 70);
    const c = setInterval(() => setShowCursor(p => !p), 500);
    return () => { clearInterval(t); clearInterval(c); };
  }, [activePage]);

  // 自定义光标
  useEffect(() => {
    const cursor = cursorRef.current, dot = cursorDotRef.current;
    if (!cursor || !dot) return;
    let mx = 0, my = 0, cx = 0, cy = 0, dx = 0, dy = 0;
    const onMove = (e) => { mx = e.clientX; my = e.clientY; };
    const anim = () => {
      cx += (mx - cx) * 0.1; cy += (my - cy) * 0.1;
      dx += (mx - dx) * 0.3; dy += (my - dy) * 0.3;
      cursor.style.transform = `translate(${cx}px,${cy}px)`;
      dot.style.transform = `translate(${dx}px,${dy}px)`;
      requestAnimationFrame(anim);
    };
    window.addEventListener('mousemove', onMove);
    anim();
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // 粒子画布
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf, mx = 0, my = 0, isTouching = false;
    let w, h, particles = [], originals = [], rotY = 0, time = 0;

    const init = () => {
      w = window.innerWidth; h = window.innerHeight;
      canvas.width = w * devicePixelRatio; canvas.height = h * devicePixelRatio;
      ctx.scale(devicePixelRatio, devicePixelRatio);
      particles = []; originals = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const x = (Math.random() - 0.5) * w * 2.2;
        const y = (Math.random() - 0.5) * h * 2.2;
        const z = Math.random() * 600 - 300;
        const p = { x, y, z };
        particles.push({ ...p }); originals.push({ ...p });
      }
    };

    const rot = (p, ry) => {
      const x = p.x * Math.cos(ry) - p.z * Math.sin(ry);
      const z = p.x * Math.sin(ry) + p.z * Math.cos(ry);
      return { x, y: p.y, z };
    };

    const draw = () => {
      ctx.fillStyle = BG_DARK; ctx.fillRect(0, 0, w, h);
      rotY += 0.0003; time += 0.02;
      const fl = 400, mi = 150;
      const sorted = particles.map((p, i) => {
        const r = rot(p, rotY);
        return { ...p, r, i };
      }).sort((a, b) => b.r.z - a.r.z);

      sorted.forEach(p => {
        const ur = rot(particles[p.i], rotY);
        const up = fl / (fl + ur.z);
        if (up <= 0) return;
        const sx = ur.x * up + w / 2;
        const sy = ur.y * up + h / 2;
        const dx = sx - mx, dy = sy - my, dist = Math.sqrt(dx*dx+dy*dy);
        const o = originals[p.i];
        if (dist < mi && mx > 0) {
          const force = (1 - dist/mi) * 30, ang = Math.atan2(dy, dx);
          particles[p.i].x = o.x + Math.cos(ang) * force;
          particles[p.i].y = o.y + Math.sin(ang) * force;
          particles[p.i].z = o.z + Math.sin(dist * 0.05) * force * 0.5;
        } else {
          particles[p.i].x += (o.x - particles[p.i].x) * 0.05;
          particles[p.i].y += (o.y - particles[p.i].y) * 0.05;
          particles[p.i].z += (o.z - particles[p.i].z) * 0.05;
        }
        const alpha = Math.min(1, up * 0.9);
        ctx.fillStyle = `${COLOR_PRIMARY}${Math.floor(alpha*255).toString(16).padStart(2,'0')}`;
        const size = Math.max(0.4, up * 1.8);
        ctx.fillRect(ur.x*up+w/2, ur.y*up+h/2, size, size);
      });

      // Hello 字 — 仅首页且未 reveal 时显示
      if (activePageRef.current === 'Home' && !homeRevealedRef.current) {
        const ba = 0.7 + 0.3*(0.5+0.5*Math.sin(time)), fy = h/2 + 4*Math.sin(time*0.8);
        const font = '100px "JetBrains Mono", monospace';
        ctx.save(); ctx.globalAlpha=ba*0.25; ctx.filter='blur(18px)'; ctx.font=font; ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillStyle=COLOR_PRIMARY; ctx.fillText('Hello',w/2,fy); ctx.restore();
        ctx.save(); ctx.globalAlpha=ba*0.5; ctx.filter='blur(6px)'; ctx.font=font; ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillStyle=COLOR_PRIMARY; ctx.fillText('Hello',w/2,fy); ctx.restore();
        ctx.save(); ctx.globalAlpha=ba; ctx.filter='none'; ctx.font=font; ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillStyle='#fff'; ctx.shadowColor=COLOR_PRIMARY; ctx.shadowBlur=20; ctx.fillText('Hello',w/2,fy); ctx.restore();
      }

      raf = requestAnimationFrame(draw);
    };

    const onMouseMove = (e) => { const r = canvas.getBoundingClientRect(); mx=e.clientX-r.left; my=e.clientY-r.top; };
    const onTouchMove = (e) => { e.preventDefault(); isTouching=true; const r=canvas.getBoundingClientRect(); mx=e.touches[0].clientX-r.left; my=e.touches[0].clientY-r.top; };
    window.addEventListener('resize', init);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', () => { isTouching=false; });
    init(); draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', init);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  // 项目数据
  const projects = [
    { id:1, type:'film', title:'HYTERA EXTREME TEST', category:'广告类', role:'导演 + 策划', brand:'Hytera 海能达 · 全球市场',
      challenge:'「螺蛳壳里做道场」。在有限预算下，无法进行外景实拍，但必须在一个棚拍空间内，具象化地展现产品在各种极端恶劣环境下的强悍性能。',
      solution:'构建 LED 屏幕背景，通过美术置景与灯光设计，模拟暴雨、沙尘暴、暴雪度等 4 大极端场景。以此反衬产品在极限压力下的稳定通讯能力。',
      link:'https://share.weiyun.com/ZM89fjFZ',
      tags:['全球市场','LED 棚拍','4 极端场景'],
      image:'/hytera-cluster.jpg',
      storyboard:['/cluster-sb-01.png','/cluster-sb-02.png','/cluster-sb-03.png'],
      actual:['/hytera-cluster.jpg','/hytera-cluster-02.jpg','/hytera-cluster-03.jpg'] },
    { id:7, type:'film', title:'HYTERA 智能巡更系统', category:'广告类', role:'导演 + 策划', brand:'Hytera 海能达 · 非洲市场',
      challenge:'客户需针对非洲市场进行精准推广。核心难点在于：需在本地（国内）制作条件下，完全剥离中文场景与东亚面孔，同时呈现产品在海外真实环境中的功能应用。',
      solution:'「AIGC 赋能跨国场景重现」。突破实拍限制，采用「实拍 + AI 生成」的混合工作流。利用 AI 生成逼真的火灾场景，解决实地拍摄的高昂成本与安全风险。',
      link:'https://share.weiyun.com/nCFAAnY6',
      tags:['非洲市场','AIGC 混合制作','跨国场景'],
      image:'/hytera-patrol.jpg',
      storyboard:['/patrol-sb-01.png','/patrol-sb-02.png','/patrol-sb-03.png'],
      actual:['/hytera-patrol.jpg','/hytera-patrol-02.jpg','/hytera-patrol-03.jpg'] },
    { id:2, type:'ai', title:'HYTERA AFRICA MARKET', category:'广告类', role:'导演 + 策划', brand:'Hytera 海能达', challenge:'需在国内制作条件下剥离中文场景与东亚面孔，呈现产品在非洲真实环境中的功能应用。', solution:'采用「实拍 + AIGC」混合工作流，AI 生成逼真场景，突破实拍限制与安全风险。', tags:['非洲市场','AIGC 制作','跨国场景'], image:'/hytera-africa.png', images:['/hytera-africa.png'], link:'https://share.weiyun.com/nCFAAnY6' },
    { id:3, type:'film', title:'龙海幼儿园 · 十周年', category:'广告类', role:'导演 + 策划', brand:'龙海幼儿园', challenge:'如何跳脱传统幼儿园宣传片的设施罗列，传递品牌独有的教育温度？', solution:'纪实抓拍手法，引导而非摆拍，捕捉真实师生互动瞬间。', tags:['品牌形象','纪实风格','十周年'], image:'/longhai.jpg', images:['/longhai.jpg','/longhai-02.jpg','/longhai-03.jpg'], link:'https://share.weiyun.com/e4BCb5ib' },
    { id:4, type:'film', title:'倾斜的秤', category:'剧情短片', role:'场记', brand:'台湾公视学生剧展', challenge:'高规格公视剧组，需精准的现场连戏记录，在紧张节奏下保持叙事连贯。', solution:'全程追踪分镜连戏细节，协助导演顺利完成制作，入选台湾公视学生剧展。', tags:['台湾公视','2024','学生剧展'], image:'/balance.jpg', images:['/balance.jpg','/balance-02.jpg','/balance-03.jpg'] },
    { id:5, type:'film', title:'有生之年', category:'电视剧', role:'制片组实习生', brand:'GTV 八大電視', challenge:'台湾 GTV 播出的长篇电视剧，学习大型商业剧组的工业化制作流程。', solution:'制片组实习，参与剧组运营与现场统筹，积累商业电视剧制片管理经验。', tags:['GTV 八大電視','台湾电视剧','2023'], image:'/living.jpg', images:['/living.jpg','/living-02.jpg','/living-03.jpg'] },
    { id:6, type:'film', title:'台湾国际女性影展', category:'影展', role:'节目剪辑实习', brand:'台湾国际女性影展', challenge:'国际影展大量节目物料后期，需在保持影展调性下高效完成。', solution:'担任节目剪辑组实习生，处理宣传片及素材剪辑，积累影展运营经验。', tags:['国际影展','节目剪辑','2023'], image:'/womens-film.jpg', images:['/womens-film.jpg','/womens-film-02.jpg','/womens-film-03.jpg'], link:'https://youtu.be/NYYNBa__myI?si=lWaOXniyniTbGPut' },
    { id:8, type:'film', title:'深圳行政执法监督码宣传片', category:'政务宣传', role:'分镜策划', brand:'深圳行政执法部门', challenge:'作为行政执法部门的普法内容，如何在完全保留严肃文本的前提下，打破政务宣传片惯有的刻板印象，提升普通受众的观看兴趣。', solution:'既然文字不能改，就在画面上下功夫。利用 IP 形象作为「视觉翻译官」，将枯燥文字转化为生动的 IP 动态传达。', tags:['政务宣传','IP形象','分镜策划'], image:'/zhifa-01.jpg', images:['/zhifa-01.jpg','/zhifa-02.jpg','/zhifa-03.jpg'], link:'https://share.weiyun.com/n2WFFdzz' },
    { id:9, type:'film', title:'海外社媒宣传策划', category:'宣传策划', role:'社媒运营 · 内容策划', brand:'FreeS Art Space 福利社', challenge:'独立艺术空间的每个展览档期，需从零开始规划全部线上宣传——在没有固定模板的情况下，为风格迥异的艺术家与作品找到各自的传播语言。', solution:'全程负责每档展览的线上宣传策划：撰写展品介绍文、现场拍摄展品图、规划内容排期、制作艺术家专访影片，独立执行后提交负责人审核。所制作的艺术家专访系列影片，最终突破账号历史最高观览量与点赞数纪录。', tags:['社媒运营','内容策划','艺术展览','专访拍摄','账号最高观览量'], image:'/exhibition-01.jpg', images:['/exhibition-01.jpg','/exhibition-02.jpg','/exhibition-03.jpg'], link:'https://share.weiyun.com/71n6nzGH' },
  ];

  // ── 各页样式（完全用 inline style，不依赖 Tailwind）
  const S = {
    page: { padding: '7rem 10vw 5rem', width: '100%', boxSizing: 'border-box', maxWidth: 1100, margin: '0 auto' },
    label: { fontSize: '.65rem', color: '#58B2DC', letterSpacing: '.4em', textTransform: 'uppercase', marginBottom: '.75rem' },
    h2: { fontSize: 'clamp(2.5rem,6vw,5rem)', fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1, margin: 0 },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid rgba(255,255,255,.05)', paddingBottom: '1rem' },
    schoolName: { fontSize: '.875rem', color: '#fff', fontWeight: 500, margin: '0 0 4px 0' },
    schoolSub: { fontSize: '.75rem', color: '#555', margin: 0 },
    period: { fontSize: '.7rem', color: '#444', whiteSpace: 'nowrap', marginLeft: '1rem' },
  };

  return (
    <div style={{ backgroundColor: BG_DARK, color: '#fff', fontFamily: "'JetBrains Mono', monospace", minHeight: '100vh' }}>

      {/* 自定义光标 */}
      <div ref={cursorRef} style={{ position:'fixed', left:0, top:0, width:40, height:40, borderRadius:'50%', background:CURSOR_COLOR, filter:'blur(8px)', opacity:.5, transform:'translate(-50%,-50%)', pointerEvents:'none', zIndex:100, mixBlendMode:'screen' }} />
      <div ref={cursorDotRef} style={{ position:'fixed', left:0, top:0, width:8, height:8, borderRadius:'50%', background:CURSOR_COLOR, transform:'translate(-50%,-50%)', pointerEvents:'none', zIndex:100, mixBlendMode:'screen' }} />

      {/* 导航 */}
      <nav style={{ position:'fixed', top:0, left:0, right:0, zIndex:50, display:'flex', justifyContent:'flex-end', alignItems:'flex-start', padding:'2rem 4rem 0' }}>
        <div style={{ display:'flex', gap:'3rem', alignItems:'center' }}>
          {['Home','About','Work','Contact'].map(page => (
            <button key={page} onClick={() => navigateTo(page)} style={{ background:'none', border:'none', outline:'none', padding:0, fontFamily:"'JetBrains Mono',monospace", fontSize:'.8rem', textTransform:'uppercase', letterSpacing:'.2em', cursor:'none', color: activePage===page ? '#fff' : '#555', position:'relative', transition:'color .2s' }}>
              {page}
              <span style={{ position:'absolute', bottom:-4, left:0, height:1, background:'#58B2DC', transition:'width .3s', width: activePage===page ? '100%' : 0 }} />
            </button>
          ))}
          {/* 跑步猫咪 */}
          {(() => {
            const [showHappy, setShowHappy] = React.useState(false);
            return (
              <div style={{ position:'relative' }}>
                <button
                  onClick={() => { setShowHappy(true); setTimeout(() => setShowHappy(false), 1500); }}
                  style={{ background:'none', border:'none', padding:0, cursor:'none', fontSize:'1.2rem', lineHeight:1, display:'block', animation:'catBounce 0.5s ease-in-out infinite' }}
                >
                  🐱
                </button>
                {showHappy && (
                  <span style={{
                    position:'absolute', top:'-1.8rem', left:'50%', transform:'translateX(-50%)',
                    fontSize:'.6rem', color:'#58B2DC', letterSpacing:'.15em', whiteSpace:'nowrap',
                    fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase',
                    animation:'fadeUp 1.5s ease forwards',
                  }}>
                    happy happy ✦
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      </nav>

      {/* 粒子 canvas — 始终 fixed 在最底层 */}
      <canvas ref={canvasRef} style={{ position:'fixed', inset:0, zIndex:0, width:'100vw', height:'100vh', opacity: (activePage==='Home' || activePage==='About') ? 1 : 0, pointerEvents: activePage==='Home' ? 'auto' : 'none', transition:'opacity 0.8s cubic-bezier(0.4,0,0.2,1)' }} />

      {/* 页面内容 */}
      <div style={{ position:'relative', zIndex:20,
        opacity: pageVisible ? 1 : 0,
        transition: pageVisible ? 'opacity 0s' : 'opacity 0.35s cubic-bezier(0.4,0,0.2,1)',
        overflow: pageVisible ? 'visible' : 'hidden',
        maxHeight: pageVisible ? 'none' : 0,
      }}>

        {/* ══ HOME ══ */}
        {displayPage === 'Home' && (
          <HomeSplit typingText={typingText} showCursor={showCursor} navigateTo={navigateTo} revealedRef={homeRevealedRef} />
        )}

        {/* ══ ABOUT ══ */}
        {displayPage === 'About' && <AboutPage />}

        {/* ══ WORK 选择页 ══ */}
        {displayPage === 'Work' && !selectedProject && !workCategory && (
          <WorkLanding onSelect={setWorkCategory} />
        )}

        {/* ══ WORK 画廊 — AI ══ */}
        {displayPage === 'Work' && !selectedProject && workCategory === 'ai' && (
          <AIGallery onBack={() => setWorkCategory(null)} />
        )}

        {/* ══ WORK 画廊 — Film ══ */}
        {displayPage === 'Work' && !selectedProject && workCategory === 'film' && (
          <div style={{ minHeight:'100vh', paddingTop:'7rem', paddingBottom:'6rem', position:'relative' }}>

            {/* 装饰性大字背景 */}
            <div style={{ position:'absolute', top:'4rem', right:'6vw', fontSize:'clamp(8rem,22vw,20rem)', fontWeight:700, letterSpacing:'-.05em', lineHeight:1, color:'rgba(255,255,255,.025)', pointerEvents:'none', userSelect:'none', zIndex:0 }}>
              WORK
            </div>

            {/* 标题区：不对称排布 */}
            <div style={{ padding:'0 10vw', marginBottom:'4rem', position:'relative', zIndex:1 }}>
              <Reveal delay={0} y={32} duration={1100}>
              <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'baseline', gap:'2rem' }}>
                  <span style={{ fontSize:'.6rem', color:'rgba(88,178,220,.5)', letterSpacing:'.5em', textTransform:'uppercase', whiteSpace:'nowrap' }}>002 —</span>
                  <span style={{ fontWeight:300, fontStyle:'italic', color:'rgba(255,255,255,.3)', fontSize:'.6rem', letterSpacing:'.5em', textTransform:'uppercase' }}>
                    {workCategory === 'film' ? 'Live Action' : 'AI Works'}
                  </span>
                </div>
                <button onClick={() => setWorkCategory(null)} style={{ background:'none', border:'none', padding:0, fontSize:'.6rem', color:'rgba(255,255,255,.25)', letterSpacing:'.3em', textTransform:'uppercase', cursor:'none', transition:'color .25s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#58B2DC'}
                  onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,.25)'}
                >← All Works</button>
              </div>
              </Reveal>
              <Reveal delay={350} y={16}>
              <div style={{ display:'flex', alignItems:'center', gap:'1.5rem', marginTop:'2rem' }}>
                <div style={{ height:1, width:40, background:'rgba(88,178,220,.3)' }} />
                <p style={{ fontSize:'.6rem', color:'rgba(255,255,255,.2)', letterSpacing:'.3em', textTransform:'uppercase', margin:0 }}>{projects.filter(p=>p.type===workCategory).length} works — hover to preview · click to explore</p>
              </div>
              </Reveal>
            </div>

            {/* 画廊 */}
            <Reveal delay={550} y={50} duration={1200}>
              <FocusGallery projects={projects.filter(p => p.type === workCategory)} onSelect={(p, idx) => { setSavedGalleryIndex(idx); setSelectedProject(p); }} initialIndex={savedGalleryIndex} />
            </Reveal>

            {/* Clients 横条：用斜线分隔，更时尚 */}
            <Reveal delay={900} y={16}>
            <div style={{ padding:'5rem 10vw 0', display:'flex', flexWrap:'wrap', gap:0, alignItems:'center', borderTop:'1px solid rgba(255,255,255,.04)', marginTop:'4rem' }}>
              <span style={{ fontSize:'.55rem', color:'rgba(255,255,255,.2)', letterSpacing:'.4em', textTransform:'uppercase', marginRight:'1.5rem' }}>Clients</span>
              {["Hytera","McDonald's","澳門記憶","SANGFOR","NeuroDance","深圳司法局","百果园"].map((b,i) => (
                <span key={i} style={{ display:'flex', alignItems:'center' }}>
                  <span style={{ fontSize:'.65rem', color:'rgba(255,255,255,.3)', letterSpacing:'.1em', padding:'0 1rem' }}>{b}</span>
                  {i < 6 && <span style={{ color:'rgba(255,255,255,.1)', fontSize:'.7rem' }}>/</span>}
                </span>
              ))}
            </div>
            </Reveal>
          </div>
        )}

        {/* ══ WORK 详情 ══ */}
        {displayPage === 'Work' && selectedProject && (
          <div style={{ padding:'5rem 10vw 12rem', maxWidth:1200, margin:'0 auto', width:'100%', boxSizing:'border-box' }}>

            {/* 返回 + 项目序号 */}
            <Reveal delay={0} y={16}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'3rem' }}>
              <button onClick={() => setSelectedProject(null)} style={{ background:'none', border:'none', outline:'none', padding:0, fontSize:'.65rem', color:'rgba(255,255,255,.25)', textTransform:'uppercase', letterSpacing:'.3em', cursor:'none', display:'flex', alignItems:'center', gap:'.75rem', transition:'color .3s' }} onMouseEnter={e=>e.currentTarget.style.color='#58B2DC'} onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,.25)'}>
                <span style={{ fontSize:'.8rem' }}>←</span> Back
              </button>
              <span style={{ fontSize:'clamp(3rem,6vw,5rem)', fontWeight:700, color:'rgba(255,255,255,.04)', letterSpacing:'-.03em' }}>
                {String(projects.findIndex(p=>p.id===selectedProject.id)+1).padStart(2,'0')}
              </span>
            </div>
            </Reveal>

            {/* 项目标题 — 超大 */}
            <Reveal delay={150} y={40} duration={1200}>
            <div style={{ marginBottom:'4rem', borderBottom:'1px solid rgba(255,255,255,.05)', paddingBottom:'2.5rem' }}>
              <p style={{ fontSize:'.55rem', color:'rgba(88,178,220,.6)', letterSpacing:'.5em', textTransform:'uppercase', margin:'0 0 1rem 0' }}>{selectedProject.category} — {selectedProject.role}</p>
              <h1 style={{ fontSize:'clamp(2.5rem,6vw,5.5rem)', fontWeight:700, letterSpacing:'-.03em', lineHeight:.9, margin:'0 0 1rem 0' }}>{selectedProject.title}</h1>
              <p style={{ fontSize:'.8rem', color:'rgba(255,255,255,.2)', margin:0, letterSpacing:'.1em' }}>{selectedProject.brand}</p>
            </div>
            </Reveal>

            {/* 主体：图片区 + 右侧文字 */}
            <div style={{ display:'grid', gridTemplateColumns:'1.1fr 1fr', gap:'5rem', alignItems:'start' }}>
              <Reveal delay={300} y={50} duration={1200}>
              {selectedProject.storyboard ? (
                /* 分镜 + 实拍双列布局 */
                <div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    {/* 列标题 */}
                    <p style={{ fontSize:'.5rem', color:'rgba(255,255,255,.25)', letterSpacing:'.4em', textTransform:'uppercase', margin:'0 0 8px 0' }}>分镜脚本</p>
                    <p style={{ fontSize:'.5rem', color:'rgba(255,255,255,.25)', letterSpacing:'.4em', textTransform:'uppercase', margin:'0 0 8px 0' }}>实际画面</p>
                    {/* 交替排列：storyboard[0], actual[0], storyboard[1], actual[1]... */}
                    {selectedProject.storyboard.map((src, n) => [
                      src
                        ? <img key={`s${n}`} src={src} alt="storyboard" style={{ width:'100%', aspectRatio:'16/9', objectFit:'cover', display:'block', opacity: n===0?1:.8 }} />
                        : <div key={`s${n}`} style={{ width:'100%', aspectRatio:'16/9', border:'1px dashed rgba(255,255,255,.08)', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ fontSize:'.5rem', color:'rgba(255,255,255,.15)', letterSpacing:'.2em' }}>—</span></div>,
                      <img key={`a${n}`} src={(selectedProject.actual||[])[n]||src} alt="actual" style={{ width:'100%', aspectRatio:'16/9', objectFit:'cover', display:'block', opacity: n===0?1:.8 }} />
                    ])}
                  </div>
                </div>
              ) : (
                /* 普通布局：1大+2小 */
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <img src={(selectedProject.images||[selectedProject.image])[0]} alt={selectedProject.title} style={{ width:'100%', aspectRatio:'16/9', objectFit:'cover', display:'block' }} />
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    {(selectedProject.images||[selectedProject.image]).slice(1).map((src,n) => (
                      <img key={n} src={src} alt="detail" style={{ width:'100%', aspectRatio:'16/9', objectFit:'cover', display:'block', opacity:.75 }} />
                    ))}
                  </div>
                </div>
              )}
              </Reveal>

              <div style={{ display:'flex', flexDirection:'column', gap:'3.5rem', paddingTop:'1rem' }}>
                <Reveal delay={500} y={30}>
                <div>
                  <p style={{ fontSize:'.55rem', color:'rgba(255,255,255,.2)', letterSpacing:'.4em', textTransform:'uppercase', marginBottom:'1rem' }}>— Challenge</p>
                  <p style={{ fontSize:'.95rem', color:'rgba(255,255,255,.7)', lineHeight:1.9, margin:0, fontWeight:300 }}>{selectedProject.challenge}</p>
                </div>
                </Reveal>
                <Reveal delay={750} y={30}>
                <div>
                  <p style={{ fontSize:'.55rem', color:'rgba(255,255,255,.2)', letterSpacing:'.4em', textTransform:'uppercase', marginBottom:'1rem' }}>— Solution</p>
                  <p style={{ fontSize:'.95rem', color:'rgba(255,255,255,.7)', lineHeight:1.9, margin:0, fontWeight:300 }}>{selectedProject.solution}</p>
                </div>
                </Reveal>
                <Reveal delay={1000} y={20}>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {selectedProject.tags.map((t,i) => (
                    <span key={i} style={{ fontSize:'.6rem', padding:'5px 14px', border:'1px solid rgba(88,178,220,.2)', color:'rgba(88,178,220,.6)', letterSpacing:'.1em' }}>{t}</span>
                  ))}
                </div>
                </Reveal>
                {selectedProject.link && (
                  <Reveal delay={1100} y={20}>
                  <div>
                    <p style={{ fontSize:'.55rem', color:'rgba(255,255,255,.2)', letterSpacing:'.4em', textTransform:'uppercase', marginBottom:'.75rem' }}>— 成片链接</p>
                    <a href={selectedProject.link} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize:'.75rem', color:'rgba(88,178,220,.7)', letterSpacing:'.05em', textDecoration:'none', borderBottom:'1px solid rgba(88,178,220,.3)', paddingBottom:'.2rem', transition:'color .2s' }}
                      onMouseEnter={e=>e.currentTarget.style.color='#58B2DC'}
                      onMouseLeave={e=>e.currentTarget.style.color='rgba(88,178,220,.7)'}
                    >{selectedProject.link}</a>
                  </div>
                  </Reveal>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══ CONTACT ══ */}
        {displayPage === 'Contact' && (
          <ContactPageWrapper />
        )}



      </div>

      {/* 底部状态栏 */}
      <div style={{ position:'fixed', bottom:'1.5rem', left:'2rem', right:'2rem', display:'flex', justifyContent:'space-between', alignItems:'center', fontFamily:"'JetBrains Mono',monospace", fontSize:'.55rem', color:'#333', textTransform:'uppercase', letterSpacing:'.2em', zIndex:30, pointerEvents:'none' }}>
        <span>22.1987° N · 113.5439° E · MACAU</span>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ width:6, height:6, borderRadius:'50%', background:'#58B2DC', display:'inline-block' }} />
          <span style={{ color:'#58B2DC' }}>System Online</span>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;700&display=swap');
        *{box-sizing:border-box}
        body{margin:0;cursor:none;overflow-x:hidden;background:${BG_DARK}}
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-track{background:${BG_DARK}}
        ::-webkit-scrollbar-thumb{background:${COLOR_PRIMARY}30;border-radius:2px}
        ::selection{background:${COLOR_PRIMARY}30}
        @media(hover:none) and (pointer:coarse){body{cursor:auto}}
      `}</style>
    </div>
  );
}
